import { useEffect, useRef, useState } from 'react'

import { ErrorModal } from './modals/errorModal'
import { LoadingIndicator } from './loadingIndicator'
import { useProject } from './project/hook'
import { useTranslator } from './translator/hook'
import './projectTitle.css'

const DELAY_FOR_SYNC = 1000 // Wait before indicating that the project is saved.

export const ProjectTitle = () => {
  const { t } = useTranslator()
  const { canEdit, saving, title, updateProjectTitle } = useProject()
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showSaving, setShowSaving] = useState(saving)
  const showSavingRef = useRef<ReturnType<typeof setTimeout>>()
  const [value, setValue] = useState(title)
  const isUpdating = useRef(false)
  const isComposing = useRef(false)

  useEffect(() => {
    if (isComposing.current) return
    setValue(title)
  }, [title])

  useEffect(() => {
    clearTimeout(showSavingRef.current)
    if (saving) {
      setShowSaving(saving)
      return
    }
    // Delay before hiding the saving indicator to allow it to sync with Studio.
    showSavingRef.current = setTimeout(() => {
      setShowSaving(false)
    }, DELAY_FOR_SYNC)
    return () => clearTimeout(showSavingRef.current)
  }, [saving])

  const handleUpdate = async () => {
    const newValue = value.trim()
    if (!newValue) {
      setError(true)
      setValue(title)
      return
    }
    setValue(newValue)
    if (newValue === title) return
    if (isUpdating.current) return
    isUpdating.current = true
    setLoading(true)
    setValue(await updateProjectTitle(newValue))
    setLoading(false)
    isUpdating.current = false
  }

  const showLoadingIcon = loading || showSaving

  return (
    <div className="project-title-container">
      <input
        className="project-title-input"
        disabled={loading || !canEdit}
        onBlur={handleUpdate}
        onChange={(e) => setValue(e.target.value)}
        onCompositionEnd={(e) => {
          isComposing.current = false
          setValue(e.currentTarget.value)
        }}
        onCompositionStart={() => {
          isComposing.current = true
        }}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return
          if (
            e.nativeEvent.isComposing ||
            isComposing.current ||
            e.keyCode === 229
          ) {
            return
          }
          handleUpdate()
        }}
        type="text"
        maxLength={20}
        value={value}
      />
      {canEdit && (
        <LoadingIndicator active={!showLoadingIcon} loading={showLoadingIcon} />
      )}
      <ErrorModal
        error={error ? t('empty_title_error') : null}
        modalTitle={t('project_update_error')}
        onClose={() => setError(false)}
      />
    </div>
  )
}
